import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { ReportTable, TopBar } from '../cc'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, type FleetId } from '../cc/fleetNav'
import { sessionDisplayName, useSession } from '../auth/useSession'
import {
  fetchMbmtAlertsSummary,
  fetchMbmtDeadMileage,
  fetchMbmtEnergyReport,
  fetchMbmtIntelReport,
  fetchMbmtLiveReport,
  fetchMbmtPunctuality,
  fetchMbmtRoutes,
  fetchMbmtScheduleIntelReport,
  fetchMbmtScheduleTripLog,
  fetchMbmtShedReport,
  fetchMbmtTripAlerts,
  fetchMbmtTripLog,
  mbmtTripAlertCount,
  MBMT_TRIP_ALERT_KINDS,
  type MbmtAlertsByDriverRow,
  type MbmtAlertsByVehicleRow,
  type MbmtAlertsSummaryRow,
  type MbmtDeadMileageRow,
  type MbmtEnergyRow,
  type MbmtIntelAlertCount,
  type MbmtIntelReportRow,
  type MbmtPunctualityRow,
  type MbmtPunctualitySummary,
  type MbmtScheduleIntelReportRow,
  type MbmtScheduleTripLogRow,
  type MbmtShedVisit,
  type MbmtTripAlertKind,
  type MbmtTripAlertRow,
  type MbmtTripLogRow,
  type MbmtVehicle,
} from '../busOps/useMbmtApi'
import type { ReportRow } from '../lib/types'
import { useDebouncedValue } from '../lib/useDebouncedValue'
import { intelVerdictColor } from '../busOps/intelVerdict'
import { formatCellDisplay, type ColumnMeta } from '../reports/columnMeta'
import { exportCsv } from '../reports/useReportData'
import {
  reportCard, reportCardPad, reportInput, reportSegment, reportSegmentBtn,
} from '../reports/ui'
import {
  MBMT_REPORT_MODES, parseMbmtReportMode,
} from './catalog'

/** Never rewrite bus cells as Ultratech Loaded/Unloaded. */
function mbmtFormatCell(value: unknown, col: string, meta: ColumnMeta): string {
  return formatCellDisplay(value, col, meta, { applyLoadStateLabels: false })
}

function istClock(now = Date.now()): string {
  return new Date(now).toLocaleTimeString('en-IN', {
    hour12: false, timeZone: 'Asia/Kolkata',
  })
}

function dayRangeLabel(from: string, to: string): string {
  return from === to ? from : `${from} to ${to}`
}

function istStamp(ms: number | null | undefined): string {
  if (ms == null) return '—'
  return new Date(ms).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

function CsvButton({ rows, columns, filename }: {
  rows: ReportRow[]
  columns: string[]
  filename: string
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={!rows.length}
      onClick={() => exportCsv(rows, columns, filename, { applyLoadStateLabels: false })}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5
                 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
    >
      <Download size={12} />
      CSV
    </button>
  )
}

function useAsyncRows<T>(loader: () => Promise<T>, deps: unknown[] = []): {
  data: T | null
  loading: boolean
  err: string | null
} {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setLoading(true)
    loader()
      .then((r) => { if (live) { setData(r); setErr(null) } })
      .catch((e: unknown) => {
        if (live) setErr(e instanceof Error ? e.message : 'load failed')
      })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return { data, loading, err }
}

function TripLogTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtTripLog(500, dateFrom, dateTo), [dateFrom, dateTo],
  )
  const rows = useMemo((): ReportRow[] => (data ?? []).map((r: MbmtTripLogRow) => {
    const kind = r.kind || (r.is_lost ? 'lost' : r.is_short ? 'short' : r.is_dead ? 'dead' : 'revenue')
    const revenue = kind === 'revenue'
    return {
    vehicle_id: r.vehicle_id,
    driver_name: r.driver_name || '—',
    schedule_code: r.schedule_code || '—',
    // tools/reconcile_site_trip_legs.py only ever assigns a route when the whole GPS
    // window gives an unambiguous answer (see definitive_route()'s own docstring) --
    // anything short of that stays null rather than a guess, so "Route entry pending"
    // is the honest label, not "—" (which would read as "no route exists").
    route_code: r.route_code || 'Route entry pending',
    direction: r.direction || '—',
    started: istStamp(r.started_ts),
    duration_min: r.duration_sec != null ? Math.round(r.duration_sec / 60) : null,
    actual_duration_min: r.actual_duration_sec != null
      ? Math.round(r.actual_duration_sec / 60) : null,
    km: r.distance_gps_km != null ? Number(r.distance_gps_km.toFixed(1)) : null,
    // TIMS's scheduled distance, plain and unannotated next to the GPS-measured Km --
    // no delta, no badge, no colour. The GPS number itself is now map-matched onto the
    // route (tools/reconcile_site_trip_legs.py's map_matched_distance_km), so this
    // column is a reference point, not a correction factor.
    scheduled_km: r.scheduled_km != null ? Number(r.scheduled_km.toFixed(1)) : null,
    soc_per_km: revenue ? (r.soc_per_km ?? null) : null,
    kwh_per_km: revenue ? (r.kwh_per_km ?? null) : null,
    discharge_kwh_per_km: revenue ? (r.discharge_kwh_per_km ?? null) : null,
    regen_kwh_per_km: revenue ? (r.regen_kwh_per_km ?? null) : null,
    regen_pct: revenue ? (r.regen_ratio_pct ?? null) : null,
    time_bucket: r.time_bucket || '—',
    kind,
    completed: r.completed ? 'yes' : 'open',
  }}), [data])
  const cols = [
    'vehicle_id', 'driver_name', 'schedule_code', 'route_code', 'direction',
    'started', 'duration_min', 'actual_duration_min', 'km', 'scheduled_km',
    'soc_per_km', 'kwh_per_km',
    'discharge_kwh_per_km', 'regen_kwh_per_km', 'regen_pct',
    'time_bucket', 'kind', 'completed',
  ]
  return (
    <ReportPane err={err} toolbar={<CsvButton rows={rows} columns={cols} filename="mbmt-duty-log" />}>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          driver_name: { label: 'Driver' },
          schedule_code: { label: 'Schedule' },
          route_code: { label: 'Route' },
          direction: { label: 'Dir' },
          started: { label: 'Started (IST)' },
          duration_min: { label: 'Sched min', align: 'right' },
          actual_duration_min: { label: 'Actual min', align: 'right' },
          km: { label: 'Km', align: 'right' },
          scheduled_km: { label: 'Sched km', align: 'right' },
          soc_per_km: { label: 'SoC/km', align: 'right' },
          kwh_per_km: { label: 'kWh/km', align: 'right' },
          discharge_kwh_per_km: { label: 'Disch/km', align: 'right' },
          regen_kwh_per_km: { label: 'Regen/km', align: 'right' },
          regen_pct: { label: 'Regen %', align: 'right' },
          time_bucket: { label: 'Bucket' },
          kind: { label: 'Kind' },
          completed: { label: 'Done' },
        }}
        rows={rows}
        emptyTitle="No duty legs yet"
        emptyBody="GPS legs matched to bronze duty trips (dead/short/lost). Rates only on revenue."
      />
    </ReportPane>
  )
}

const INTEL_TIME_BUCKETS = ['early', 'morning', 'afternoon', 'peak', 'night'] as const
const INTEL_BUCKET_LABEL: Record<string, string> = {
  early: 'Early (05–08)', morning: 'Morning (08–12)', afternoon: 'Afternoon (12–16)',
  peak: 'Peak (16–20)', night: 'Night (20–05)',
}
interface IntelPivotCell {
  p50: number | null
  p85: number | null
  schedMin: number | null
  verdict: string | null
}

/** 2 decimal places, always -- "4" next to "16.05" reads as two different precisions
 * for the same kind of number. null/undefined render as an em dash, never "null". */
function fmt2(n: number | null | undefined): string {
  return n == null ? '—' : n.toFixed(2)
}

interface IntelRoutePivot {
  route: string
  rows: { direction: string; cells: Partial<Record<string, IntelPivotCell>> }[]
  alertsByBucket: Partial<Record<string, number>>
}

/** Route is the primary read axis; direction x time-of-day is the analysis grid inside
 * it. A flat list sorted by n can't answer "how does this route's duration change
 * through the day" or "is Up different from Down" without manually cross-referencing
 * rows scattered across the whole table -- this pivots that same data so both
 * questions are a single glance. Vehicletype (bus class) is aggregated out of the grid
 * (n-weighted, same technique the backend already uses to drop congestion) since it
 * isn't the axis anyone asked to read here; the CSV export keeps the full breakdown.
 *
 * alertCounts has no direction axis (site_ours_alert carries none), so it lands as an
 * extra "Alerts" row per route rather than a per-direction cell. */
function buildIntelPivot(
  rows: MbmtIntelReportRow[], alertCounts: MbmtIntelAlertCount[] = [],
): IntelRoutePivot[] {
  const byKey = new Map<string, {
    route: string; direction: string; bucket: string
    n: number; p50Sum: number; p85Sum: number; schedMin: number | null; verdict: string | null
  }>()
  for (const r of rows) {
    const key = `${r.route_code} ${r.direction} ${r.time_bucket}`
    const agg = byKey.get(key) ?? {
      route: r.route_code, direction: r.direction, bucket: r.time_bucket,
      n: 0, p50Sum: 0, p85Sum: 0, schedMin: null, verdict: null,
    }
    agg.n += r.n
    agg.p50Sum += (r.p50_min ?? 0) * r.n
    agg.p85Sum += (r.p85_min ?? 0) * r.n
    // scheduled_min/verdict are computed once per (route, direction), identical
    // across every vehicletype grain sharing it -- take the first one seen.
    if (agg.schedMin == null && r.scheduled_min != null) {
      agg.schedMin = r.scheduled_min
      agg.verdict = r.verdict
    }
    byKey.set(key, agg)
  }

  const routeMap = new Map<string, Map<string, Partial<Record<string, IntelPivotCell>>>>()
  for (const agg of byKey.values()) {
    if (!routeMap.has(agg.route)) routeMap.set(agg.route, new Map())
    const dirMap = routeMap.get(agg.route)!
    if (!dirMap.has(agg.direction)) dirMap.set(agg.direction, {})
    dirMap.get(agg.direction)![agg.bucket] = {
      p50: agg.n ? agg.p50Sum / agg.n : null,
      p85: agg.n ? agg.p85Sum / agg.n : null,
      schedMin: agg.schedMin,
      verdict: agg.verdict,
    }
  }

  const alertsByRoute = new Map<string, Partial<Record<string, number>>>()
  for (const a of alertCounts) {
    const byBucket = alertsByRoute.get(a.route_code) ?? {}
    byBucket[a.time_bucket] = (byBucket[a.time_bucket] ?? 0) + a.n
    alertsByRoute.set(a.route_code, byBucket)
  }

  const routes = new Set([...routeMap.keys(), ...alertsByRoute.keys()])
  return [...routes].sort((a, b) => {
    const na = Number(a)
    const nb = Number(b)
    return Number.isNaN(na) || Number.isNaN(nb) ? a.localeCompare(b) : na - nb
  }).map((route) => ({
    route,
    rows: [...(routeMap.get(route)?.entries() ?? [])]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([direction, cells]) => ({ direction, cells })),
    alertsByBucket: alertsByRoute.get(route) ?? {},
  }))
}

function RouteScheduleIntelTable(): JSX.Element {
  const { data, loading, err } = useAsyncRows(() => fetchMbmtIntelReport(), [])
  const { data: routeNames } = useAsyncRows(
    async () => new Map((await fetchMbmtRoutes()).routes.map((r) => [r.route_code, r.name])),
    [],
  )
  const rows = data?.rows ?? []
  const alerts = data?.alerts ?? []
  const flatRows = useMemo((): ReportRow[] => rows.map((r: MbmtIntelReportRow) => ({
    route_code: r.route_code,
    direction: r.direction,
    time_bucket: r.time_bucket,
    vehicletype: r.vehicletype,
    n: r.n,
    p50_min: r.p50_min,
    p85_min: r.p85_min,
    scheduled_min: r.scheduled_min,
    verdict: r.verdict,
  })), [rows])
  const pivot = useMemo(() => buildIntelPivot(rows, alerts), [rows, alerts])
  const risky = flatRows.filter((r) => r.verdict === 'hazardous' || r.verdict === 'short').length
  const cols = [
    'route_code', 'direction', 'time_bucket', 'vehicletype',
    'n', 'p50_min', 'p85_min', 'scheduled_min', 'verdict',
  ]
  return (
    <ReportPane
      err={err}
      toolbar={<CsvButton rows={flatRows} columns={cols} filename="mbmt-route-schedule-intel" />}
      note={risky
        ? `${risky} grain${risky === 1 ? '' : 's'} flagged short or hazardous — take these to the authority.`
        : 'No short or hazardous grains in the current rollup.'}
    >
      {loading && <p className="text-[12px] text-slate-500">Loading…</p>}
      {!loading && pivot.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <p className="text-[13px] font-semibold text-slate-700">No route intel yet</p>
          <p className="mt-1 text-[12px] text-slate-500">
            site_route_intel fills after closed legs roll up by route, direction,
            time-of-day and bus model.
          </p>
        </div>
      )}
      {!loading && pivot.length > 0 && (
        <div className="flex flex-col gap-3 overflow-y-auto">
          {pivot.map((rp) => {
            const name = routeNames?.get(rp.route)
            const hasAlerts = INTEL_TIME_BUCKETS.some((b) => rp.alertsByBucket[b] != null)
            return (
              <div key={rp.route} className={`${reportCard} ${reportCardPad} shrink-0`}>
                <p className="mb-2 text-[12px] font-semibold text-slate-800">
                  Route {rp.route}{name ? ` — ${name}` : ''}
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-[11px]">
                    <thead>
                      <tr className="text-slate-500">
                        <th className="px-2 py-1">Dir</th>
                        {INTEL_TIME_BUCKETS.map((b) => (
                          <th key={b} className="px-2 py-1 font-normal">{INTEL_BUCKET_LABEL[b]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rp.rows.map((row) => (
                        <tr key={row.direction} className="border-t border-slate-100">
                          <td className="px-2 py-1 align-top font-medium text-slate-800">{row.direction}</td>
                          {INTEL_TIME_BUCKETS.map((b) => {
                            const cell = row.cells[b]
                            if (!cell) {
                              return <td key={b} className="px-2 py-1 align-top text-slate-300">—</td>
                            }
                            return (
                              <td key={b} className="px-2 py-1 align-top font-mono tabular-nums">
                                <div className="text-slate-800">
                                  Actual {fmt2(cell.p50)}–{fmt2(cell.p85)} min
                                </div>
                                {cell.schedMin != null && (
                                  <div className={intelVerdictColor(cell.verdict)}>
                                    Scheduled {fmt2(cell.schedMin)} min
                                  </div>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                      {hasAlerts && (
                        <tr className="border-t border-slate-100">
                          <td className="px-2 py-1 align-top font-medium text-slate-800">Alerts</td>
                          {INTEL_TIME_BUCKETS.map((b) => {
                            const n = rp.alertsByBucket[b]
                            return (
                              <td key={b} className="px-2 py-1 align-top font-mono tabular-nums text-slate-600">
                                {n != null ? `${n} alert${n === 1 ? '' : 's'}` : '—'}
                              </td>
                            )
                          })}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
          <p className="shrink-0 text-[11px] text-slate-500">
            <strong>Actual</strong> = measured p50–p85 minutes (real GPS travel time).
            <strong> Scheduled</strong> = the timetable's own planned minutes for that
            trip. <strong>short</strong> = scheduled &lt; p50 · <strong>tricky</strong> =
            between p50 and p85 · <strong>ok</strong> = scheduled covers p85. Bus class
            (vehicletype) is averaged out of this grid; the CSV export keeps the full
            per-class breakdown and leg counts. <strong>Alerts</strong> is undirected
            (site_ours_alert has no direction column) and only shows on routes with at
            least one in the trailing 90 days.
          </p>
        </div>
      )}
    </ReportPane>
  )
}

/** Schedule is already the primary read axis at this grain -- unlike route intel
 * above, no pivot needed: each row already is one schedule × time-of-day, with
 * route/direction carried as metadata rather than something to fold multiple rows
 * into. On direct instruction: operations wants a SPECIFIC schedule's own
 * expected-vs-actual, not a route's blended average across every schedule that runs
 * it (see site_schedule_intel's own schema comment). */
function ScheduleIntelTable(): JSX.Element {
  const { data, loading, err } = useAsyncRows(() => fetchMbmtScheduleIntelReport(), [])
  const rows = data ?? []
  const flatRows = useMemo((): ReportRow[] => rows.map((r: MbmtScheduleIntelReportRow) => ({
    schedule_code: r.schedule_code,
    route_code: r.route_code || 'Route entry pending',
    direction: r.direction || '—',
    time_bucket: r.time_bucket,
    n: r.n,
    p50_min: r.p50_min,
    p85_min: r.p85_min,
    scheduled_min: r.scheduled_min,
    verdict: r.verdict,
  })), [rows])
  const risky = flatRows.filter((r) => r.verdict === 'hazardous' || r.verdict === 'short').length
  const cols = [
    'schedule_code', 'route_code', 'direction', 'time_bucket',
    'n', 'p50_min', 'p85_min', 'scheduled_min', 'verdict',
  ]
  return (
    <ReportPane
      err={err}
      toolbar={<CsvButton rows={flatRows} columns={cols} filename="mbmt-schedule-intel" />}
      note={risky
        ? `${risky} schedule${risky === 1 ? '' : 's'} flagged short or hazardous — take these to the authority.`
        : 'No short or hazardous schedules in the current rollup.'}
    >
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          schedule_code: { label: 'Schedule', sticky: true },
          route_code: { label: 'Route' },
          direction: { label: 'Dir' },
          time_bucket: { label: 'Bucket' },
          n: { label: 'N', align: 'right' },
          p50_min: { label: 'Actual p50', align: 'right' },
          p85_min: { label: 'Actual p85', align: 'right' },
          scheduled_min: { label: 'Scheduled min', align: 'right' },
          verdict: { label: 'Verdict' },
        }}
        rows={flatRows}
        emptyTitle="No schedule intel yet"
        emptyBody="site_schedule_intel fills after closed legs whose (schedule_id, schedule_trip_id) joined TIMS's own schedule template roll up by schedule code and time-of-day."
      />
    </ReportPane>
  )
}

function LiveTripsTable(): JSX.Element {
  const { data, loading, err } = useAsyncRows(() => fetchMbmtLiveReport(), [])
  const rows = useMemo((): ReportRow[] => (data ?? []).map((v: MbmtVehicle) => ({
    vehicle_id: v.vehicle_id,
    driver_name: v.driver_name || '—',
    schedule_code: v.schedule_code || '—',
    schedule_shift: v.schedule_shift || '—',
    route_code: v.route_code || '—',
    soc_pct: v.soc_pct != null ? Math.round(v.soc_pct) : null,
    speed_kph: v.speed_kph != null ? Math.round(v.speed_kph) : null,
    status: v.on_air ? (v.moving ? 'moving' : 'idle') : 'offline',
    seen_s: Math.round(v.seen_s),
  })), [data])
  const cols = [
    'vehicle_id', 'driver_name', 'schedule_code', 'schedule_shift', 'route_code',
    'soc_pct', 'speed_kph', 'status', 'seen_s',
  ]
  return (
    <ReportPane err={err} toolbar={<CsvButton rows={rows} columns={cols} filename="mbmt-live-trips" />}>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          driver_name: { label: 'Driver' },
          schedule_code: { label: 'Schedule' },
          schedule_shift: { label: 'Shift' },
          route_code: { label: 'Route' },
          soc_pct: { label: 'SoC %', align: 'right' },
          speed_kph: { label: 'kph', align: 'right' },
          status: { label: 'Status' },
          seen_s: { label: 'Seen s', align: 'right' },
        }}
        rows={rows}
        emptyTitle="No on-duty buses"
        emptyBody="Live roster is empty until telemetry and TIMS duties land."
      />
    </ReportPane>
  )
}

function ShedTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtShedReport(dateFrom, dateTo), [dateFrom, dateTo],
  )
  const visitRows = useMemo((): ReportRow[] => (data?.visits ?? []).map((v: MbmtShedVisit) => ({
    vehicle_id: v.vehicle_id,
    name: v.name || v.stage_id,
    entered: istStamp(v.entered_ts),
    exited: istStamp(v.exited_ts),
    duration_min: v.duration_sec != null ? Math.round(v.duration_sec / 60) : 'inside',
  })), [data])
  const schedRows = useMemo((): ReportRow[] => (data?.scheduled ?? []).map((r) => ({
    vehicle_id: r.vehicle_id,
    kind: r.kind,
    schedule_code: r.schedule_code || '—',
    driver_name: r.driver_name || '—',
    scheduled: istStamp(r.scheduled_ts),
    done: r.kind === 'arrival'
      ? (r.arrived ? 'arrived' : 'pending')
      : (r.departed ? 'departed' : 'pending'),
  })), [data])
  const visitCols = ['vehicle_id', 'name', 'entered', 'exited', 'duration_min']
  return (
    <ReportPane
      err={err}
      note={data
        ? `${dayRangeLabel(data.date_from, data.date_to)} · ${data.at_depot_now.length} inside depot now · ${visitRows.length} GPS visits · ${schedRows.length} scheduled sheds`
        : undefined}
      toolbar={<CsvButton rows={visitRows} columns={visitCols} filename="mbmt-shed-visits" />}
    >
      <p className="mb-2 text-[12px] font-semibold text-slate-700">GPS depot visits</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={visitCols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          name: { label: 'Stage' },
          entered: { label: 'Entered' },
          exited: { label: 'Exited' },
          duration_min: { label: 'Min' },
        }}
        rows={visitRows}
        emptyTitle="No depot visits today"
        emptyBody="site_stage_visit records ENTER/EXIT at the depot polygon."
      />
      <p className="mb-2 mt-4 text-[12px] font-semibold text-slate-700">Scheduled inshed / outshed</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading}
        columns={['vehicle_id', 'kind', 'schedule_code', 'driver_name', 'scheduled', 'done']}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          kind: { label: 'Kind' },
          schedule_code: { label: 'Schedule' },
          driver_name: { label: 'Driver' },
          scheduled: { label: 'Scheduled' },
          done: { label: 'State' },
        }}
        rows={schedRows}
        emptyTitle="No scheduled sheds in window"
        emptyBody="TIMS duty start/end times for the day."
      />
    </ReportPane>
  )
}

function PunctualityTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtPunctuality(dateFrom, dateTo), [dateFrom, dateTo],
  )
  const summaryRows = useMemo((): ReportRow[] => (data?.summary ?? []).map((r: MbmtPunctualitySummary) => ({
    route_code: r.route_code,
    kind: r.kind,
    n: r.n,
    late: r.late,
    early: r.early,
    on_time: r.on_time,
    avg_delta_min: r.avg_delta_min,
  })), [data])
  const detailRows = useMemo((): ReportRow[] => (data?.rows ?? []).map((r: MbmtPunctualityRow) => ({
    vehicle_id: r.vehicle_id,
    route_code: r.route_code || '—',
    kind: r.kind,
    schedule_code: r.schedule_code || '—',
    scheduled: istStamp(r.scheduled_ts),
    actual: istStamp(r.actual_ts),
    delta_min: r.delta_min,
    status: r.status,
  })), [data])
  const detailCols = [
    'vehicle_id', 'route_code', 'kind', 'schedule_code', 'scheduled', 'actual', 'delta_min', 'status',
  ]
  return (
    <ReportPane
      err={err}
      note={data
        ? `IST ${dayRangeLabel(data.date_from, data.date_to)}. Actuals must fall within ~3h of the scheduled shed; multi-hour mismatches are dropped.`
        : undefined}
      toolbar={<CsvButton rows={detailRows} columns={detailCols} filename="mbmt-punctuality" />}
    >
      <p className="mb-2 text-[12px] font-semibold text-slate-700">By route</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading}
        columns={['route_code', 'kind', 'n', 'late', 'early', 'on_time', 'avg_delta_min']}
        columnDefs={{
          route_code: { label: 'Route', sticky: true },
          kind: { label: 'Kind' },
          n: { label: 'n', align: 'right' },
          late: { label: 'Late', align: 'right' },
          early: { label: 'Early', align: 'right' },
          on_time: { label: 'On time', align: 'right' },
          avg_delta_min: { label: 'Avg Δ min', align: 'right' },
        }}
        rows={summaryRows}
        accentRow={(r) => Number(r.late) > 0}
        emptyTitle="No confirmed sheds yet"
        emptyBody="Punctuality needs a confirmed actual (leg start/end or depot visit)."
      />
      <p className="mb-2 mt-4 text-[12px] font-semibold text-slate-700">Every confirmed event</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={detailCols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          route_code: { label: 'Route' },
          kind: { label: 'Kind' },
          schedule_code: { label: 'Schedule' },
          scheduled: { label: 'Scheduled' },
          actual: { label: 'Actual' },
          delta_min: { label: 'Δ min', align: 'right' },
          status: { label: 'Status' },
        }}
        rows={detailRows}
        accentRow={(r) => r.status === 'late'}
        emptyTitle="No rows"
        emptyBody=""
      />
    </ReportPane>
  )
}

function AlertsSummaryTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const [vehicleId, setVehicleId] = useState('')
  const [driverName, setDriverName] = useState('')
  // Debounced: both filters are exact-match against free text, so a half-typed plate
  // or name would otherwise fire one request per keystroke and flash "No alerts" until
  // the full value lands.
  const vehicleIdDebounced = useDebouncedValue(vehicleId.trim())
  const driverNameDebounced = useDebouncedValue(driverName.trim())
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtAlertsSummary(dateFrom, dateTo, vehicleIdDebounced, driverNameDebounced),
    [dateFrom, dateTo, vehicleIdDebounced, driverNameDebounced],
  )
  const rows = useMemo((): ReportRow[] => (data?.rows ?? []).map((r: MbmtAlertsSummaryRow) => ({
    kind: r.kind,
    category: r.category,
    route_code: r.route_code,
    n: r.n,
    critical: r.critical,
  })), [data])
  const vehicleRows = useMemo((): ReportRow[] => (data?.by_vehicle ?? []).map((r: MbmtAlertsByVehicleRow) => ({
    vehicle_id: r.vehicle_id,
    n: r.n,
    critical: r.critical,
  })), [data])
  const driverRows = useMemo((): ReportRow[] => (data?.by_driver ?? []).map((r: MbmtAlertsByDriverRow) => ({
    // Just "Unknown", not "Unknown (historical)" -- a blank driver_name now means TIMS
    // has no matching duty record for that vehicle/day/time, which can happen on ANY
    // day (today included, e.g. a roster fetch hiccup), not only historical ones.
    driver_name: r.driver_name || 'Unknown',
    n: r.n,
    critical: r.critical,
  })), [data])
  const cols = ['kind', 'category', 'route_code', 'n', 'critical']
  const vehicleCols = ['vehicle_id', 'n', 'critical']
  const driverCols = ['driver_name', 'n', 'critical']
  return (
    <ReportPane
      err={err}
      note={data ? `${dayRangeLabel(data.date_from, data.date_to)} · ${data.total} alerts` : undefined}
      toolbar={(
        <div className="flex items-center gap-2">
          <CsvButton rows={rows} columns={cols} filename="mbmt-alerts-summary" />
          <Link
            to={data?.live_path || '/bus-ops/alerts'}
            className="rounded-lg bg-[#0F172A] px-2.5 py-1.5 text-[11px] font-semibold text-white"
          >
            Open Live Alerts
          </Link>
        </div>
      )}
    >
      <div className="mb-2 flex shrink-0 flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          vehicle
          <input
            type="text" value={vehicleId} placeholder="e.g. MH04LY1"
            onChange={(e) => setVehicleId(e.target.value)}
            className={reportInput}
          />
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          driver
          <input
            type="text" value={driverName} placeholder="e.g. Ramesh"
            onChange={(e) => setDriverName(e.target.value)}
            className={reportInput}
          />
        </label>
      </div>
      <p className="mb-2 text-[12px] font-semibold text-slate-700">By kind × route</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          kind: { label: 'Kind', sticky: true },
          category: { label: 'Category' },
          route_code: { label: 'Route' },
          n: { label: 'Count', align: 'right' },
          critical: { label: 'Critical', align: 'right' },
        }}
        rows={rows}
        accentRow={(r) => Number(r.critical) > 0}
        emptyTitle="No alerts in window"
        emptyBody="site_ours_alert + site_netradyne_alert, excluding superseded rows."
      />
      <p className="mb-2 mt-4 text-[12px] font-semibold text-slate-700">By vehicle</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={vehicleCols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          n: { label: 'Count', align: 'right' },
          critical: { label: 'Critical', align: 'right' },
        }}
        rows={vehicleRows}
        accentRow={(r) => Number(r.critical) > 0}
        emptyTitle="No alerts in window"
        emptyBody=""
      />
      <p className="mb-2 mt-4 text-[12px] font-semibold text-slate-700">By driver</p>
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={driverCols}
        columnDefs={{
          driver_name: { label: 'Driver', sticky: true },
          n: { label: 'Count', align: 'right' },
          critical: { label: 'Critical', align: 'right' },
        }}
        rows={driverRows}
        accentRow={(r) => Number(r.critical) > 0}
        emptyTitle="No alerts in window"
        emptyBody=""
      />
      <p className="mt-2 shrink-0 text-[11px] text-slate-500">
        Driver attribution comes from the TIMS duty roster for that alert's own day —
        "Unknown" means TIMS has no matching duty record for that vehicle at that time,
        never a guess.
      </p>
    </ReportPane>
  )
}

function EnergyTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtEnergyReport(500, dateFrom, dateTo), [dateFrom, dateTo],
  )
  const rows = useMemo((): ReportRow[] => (data ?? []).map((r: MbmtEnergyRow) => ({
    vehicle_id: r.vehicle_id,
    day: istStamp(r.started_ts).split(',')[0] ?? '—',
    route_code: r.route_code || (r.n_legs && r.n_legs > 1 ? 'multiple' : '—'),
    n_legs: r.n_legs ?? 1,
    km: r.distance_gps_km != null ? Number(r.distance_gps_km.toFixed(1)) : null,
    soc_start: r.soc_start,
    soc_end: r.soc_end,
    energy_kwh: r.energy_kwh,
    soc_per_km: r.soc_per_km,
    kwh_per_km: r.kwh_per_km,
    discharge_kwh_per_km: r.discharge_kwh_per_km ?? null,
    regen_kwh_per_km: r.regen_kwh_per_km ?? null,
    regen_pct: r.regen_ratio_pct ?? null,
  })), [data])
  const cols = [
    'vehicle_id', 'day', 'route_code', 'n_legs', 'km',
    'soc_start', 'soc_end', 'energy_kwh', 'soc_per_km', 'kwh_per_km',
    'discharge_kwh_per_km', 'regen_kwh_per_km', 'regen_pct',
  ]
  return (
    <ReportPane
      err={err}
      note="One row per bus per day, summing every leg it ran that day -- a single duty is often scheduled as many short back-and-forth hops, so per-leg km read as too small to mean anything on their own. SoC/km and kWh/km are SoC-delta x pack size over GPS distance, filled in roughly every 10 minutes by the reconciliation job -- not a direct discharge/regen measurement, so discharge_kwh_per_km/regen_kwh_per_km/regen_pct stay blank for MBMT."
      toolbar={<CsvButton rows={rows} columns={cols} filename="mbmt-energy" />}
    >
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          day: { label: 'Day' },
          route_code: { label: 'Route' },
          n_legs: { label: 'Legs', align: 'right' },
          km: { label: 'Km (whole day)', align: 'right' },
          soc_start: { label: 'SoC in', align: 'right' },
          soc_end: { label: 'SoC out', align: 'right' },
          energy_kwh: { label: 'kWh', align: 'right' },
          soc_per_km: { label: 'SoC/km', align: 'right' },
          kwh_per_km: { label: 'kWh/km', align: 'right' },
          discharge_kwh_per_km: { label: 'Disch/km', align: 'right' },
          regen_kwh_per_km: { label: 'Regen/km', align: 'right' },
          regen_pct: { label: 'Regen %', align: 'right' },
        }}
        rows={rows}
        emptyTitle="No energy-stamped legs yet"
        emptyBody="Deploy the Wave 3 closer and apply site_trip_leg SoC columns, then closed legs fill this table."
      />
    </ReportPane>
  )
}

const TRIP_ALERT_KIND_LABEL: Record<MbmtTripAlertKind, string> = {
  HARSH_BRAKE: 'Harsh brake',
  HARSH_ACCEL: 'Harsh accel',
  OVERSPEED: 'Overspeed',
  UNSCHEDULED_STOP: 'Unsched. stop',
  EXCESSIVE_IDLING: 'Idling',
}

const TRIP_ALERTS_PAGE_SIZE = 100

function TripAlertsReportTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const [offset, setOffset] = useState(0)
  // A new date range makes the previous offset meaningless (and possibly past the end
  // of the new range's own total) -- same reason MbmtTripAlerts.tsx resets its own
  // offset on a date change.
  useEffect(() => { setOffset(0) }, [dateFrom, dateTo])
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtTripAlerts({ dateFrom, dateTo, limit: TRIP_ALERTS_PAGE_SIZE, offset }),
    [dateFrom, dateTo, offset],
  )
  const rows = useMemo((): ReportRow[] => (data?.rows ?? []).map((r: MbmtTripAlertRow) => ({
    vehicle_id: r.vehicle_id,
    driver_name: r.driver_name || '—',
    schedule_code: r.schedule_code || '—',
    route: r.route || '—',
    started: istStamp(r.started_ts),
    ended: istStamp(r.ended_ts),
    ...Object.fromEntries(MBMT_TRIP_ALERT_KINDS.map((k) => [k, mbmtTripAlertCount(r, k)])),
    total_alerts: r.total_alerts,
  })), [data])
  const cols = [
    'vehicle_id', 'driver_name', 'schedule_code', 'route', 'started', 'ended',
    ...MBMT_TRIP_ALERT_KINDS, 'total_alerts',
  ]
  const total = data?.total ?? 0
  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + rows.length, total)
  return (
    <ReportPane
      err={err}
      note={data ? `${pageStart}–${pageEnd} of ${total} trips` : undefined}
      toolbar={(
        <div className="flex items-center gap-2">
          {/* Exports only the rows on this page -- labelled, not silently truncated,
              since a vehicle/day combo at MBMT can run well past one page. */}
          <CsvButton rows={rows} columns={cols} filename="mbmt-trip-alerts-page" />
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - TRIP_ALERTS_PAGE_SIZE))}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={offset + TRIP_ALERTS_PAGE_SIZE >= total}
            onClick={() => setOffset((o) => o + TRIP_ALERTS_PAGE_SIZE)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    >
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          driver_name: { label: 'Driver' },
          schedule_code: { label: 'Schedule' },
          route: { label: 'Route' },
          started: { label: 'Started (IST)' },
          ended: { label: 'Ended (IST)' },
          ...Object.fromEntries(
            MBMT_TRIP_ALERT_KINDS.map((k) => [k, { label: TRIP_ALERT_KIND_LABEL[k], align: 'right' as const }]),
          ),
          total_alerts: { label: 'Total', align: 'right' },
        }}
        rows={rows}
        accentRow={(r) => Number(r.total_alerts) > 0}
        emptyTitle="No scheduled trips in window"
        emptyBody="v_mbmt_trip_alerts: one row per site_duty_trip slot, with driving-behaviour alerts matched into its window."
      />
    </ReportPane>
  )
}

function DeadMileageTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtDeadMileage(dateFrom, dateTo), [dateFrom, dateTo],
  )
  const rows = useMemo((): ReportRow[] => (data?.rows ?? []).map((r: MbmtDeadMileageRow) => ({
    vehicle_id: r.vehicle_id,
    iso_week: r.iso_week,
    scheduled_km: Number(r.scheduled_km.toFixed(1)),
    dead_km: Number(r.dead_km.toFixed(1)),
    dead_pct: r.dead_pct,
    short_km: Number(r.short_km.toFixed(1)),
    short_pct: r.short_pct,
    lost_km: Number(r.lost_km.toFixed(1)),
    lost_pct: r.lost_pct,
  })), [data])
  const cols = [
    'vehicle_id', 'iso_week', 'scheduled_km', 'dead_km', 'dead_pct',
    'short_km', 'short_pct', 'lost_km', 'lost_pct',
  ]
  return (
    <ReportPane
      err={err}
      note={data ? dayRangeLabel(data.date_from, data.date_to) : undefined}
      toolbar={<CsvButton rows={rows} columns={cols} filename="mbmt-dead-mileage" />}
    >
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          iso_week: { label: 'ISO week' },
          scheduled_km: { label: 'Scheduled km', align: 'right' },
          dead_km: { label: 'Dead km', align: 'right' },
          dead_pct: { label: 'Dead %', align: 'right' },
          short_km: { label: 'Short km', align: 'right' },
          short_pct: { label: 'Short %', align: 'right' },
          lost_km: { label: 'Lost km', align: 'right' },
          lost_pct: { label: 'Lost %', align: 'right' },
        }}
        rows={rows}
        accentRow={(r) => (
          (r.dead_pct !== null && Number(r.dead_pct) > 10)
          || (r.lost_pct !== null && Number(r.lost_pct) > 10)
          // pct is null when the group's own scheduled km is 0 -- still worth a look
          // if dead/lost km exist with nothing to measure them against.
          || (r.dead_pct === null && Number(r.dead_km) > 0)
          || (r.lost_pct === null && Number(r.lost_km) > 0)
        )}
        emptyTitle="No duty rows in window"
        emptyBody="site_duty_trip: is_dead / is_short / is_lost against distance_km (TIMS's own scheduled distance per trip), by vehicle and ISO week."
      />
    </ReportPane>
  )
}

function ScheduleTripLogTable({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }): JSX.Element {
  const { data, loading, err } = useAsyncRows(
    () => fetchMbmtScheduleTripLog(dateFrom, dateTo), [dateFrom, dateTo],
  )
  const rows = useMemo((): ReportRow[] => (data?.rows ?? []).map((r: MbmtScheduleTripLogRow) => ({
    vehicle_id: r.vehicle_id,
    schedule_code: r.schedule_code || '—',
    day: r.day,
    tims_start_time: istStamp(r.tims_start_time),
    cc_start_time: istStamp(r.cc_start_time),
    tims_end_time: istStamp(r.tims_end_time),
    cc_end_time: istStamp(r.cc_end_time),
  })), [data])
  const cols = [
    'vehicle_id', 'schedule_code', 'day',
    'tims_start_time', 'cc_start_time', 'tims_end_time', 'cc_end_time',
  ]
  return (
    <ReportPane
      err={err}
      note={data ? `${dayRangeLabel(data.date_from, data.date_to)} · ${data.count} trips` : undefined}
      toolbar={<CsvButton rows={rows} columns={cols} filename="mbmt-schedule-trip-log" />}
    >
      <ReportTable
        formatCell={mbmtFormatCell}
        applyLoadStateLabels={false}
        fill compact loading={loading} columns={cols}
        columnDefs={{
          vehicle_id: { label: 'Plate', sticky: true },
          schedule_code: { label: 'Schedule' },
          day: { label: 'Day' },
          tims_start_time: { label: 'TIMS start' },
          cc_start_time: { label: 'CC start' },
          tims_end_time: { label: 'TIMS end' },
          cc_end_time: { label: 'CC end' },
        }}
        rows={rows}
        emptyTitle="No scheduled trips in window"
        emptyBody="TIMS planned start/end (tims_*) vs GPS depot-boundary evidence (cc_*), one row per scheduled trip."
      />
    </ReportPane>
  )
}

function ReportPane({
  children, err, toolbar, note,
}: {
  children: ReactNode
  err: string | null
  toolbar?: ReactNode
  note?: string
}): JSX.Element {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {(note || toolbar) && (
        <div className="flex shrink-0 items-center justify-between gap-3">
          {note ? <p className="text-[12px] text-slate-500">{note}</p> : <span />}
          {toolbar}
        </div>
      )}
      {err && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">{err}</p>
      )}
      {children}
    </div>
  )
}

/** Report modes that read a historical range rather than "right now" -- Live trips has
 * no date concept at all, and Intel/Alerts aren't built to take one yet (see
 * MbmtReportsShell's own history for why those two were left out of this pass). */
const DATE_RANGE_MODES = new Set([
  'trips', 'shed', 'punctuality', 'energy', 'alerts',
  'trip-alerts', 'dead-mileage', 'schedule-trips',
])

function todayIst(): string {
  // Same convention as the rest of this console (BreakdownSnapshot.tsx, TatSection.tsx)
  // -- UTC "today", not IST-precise right at the midnight boundary. Good enough for a
  // date-range default a user can always widen.
  return new Date().toISOString().slice(0, 10)
}

function yesterdayIst(): string {
  return new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)
}

export function MbmtReportsShell(): JSX.Element {
  const navigate = useNavigate()
  const { mode: modeParam } = useParams<{ mode?: string }>()
  const mode = parseMbmtReportMode(modeParam)
  const session = useSession()
  const isSuperAdmin = session.status === 'signed-in' && session.session.role === 'super_admin'
  const [clock, setClock] = useState(() => istClock())
  // Default to yesterday..today, not just today: the bronze TIMS schedule sync
  // (sync-site-duty-trips) lags the live day by several hours -- confirmed live, still
  // zero site_duty_trip rows for today at 10:20am IST while yesterday's ~1350 rows were
  // long complete. A same-day-only default read as "the report is broken" every
  // morning; yesterday's complete data now anchors the view, with today's rows (however
  // few exist so far) still included rather than hidden. Alerts has no such lag --
  // site_ours_alert/site_netradyne_alert are written live by the streaming pipeline, not
  // synced from TIMS -- so defaulting it to the same 2-day window just meant this report's
  // total never matched the landing board's "Alerts today" KPI (actionables()'s
  // today_alert_count, strictly today's IST window) unless someone clicked "Today" first.
  const [dateFrom, setDateFrom] = useState(mode === 'alerts' ? todayIst : yesterdayIst)
  const [dateTo, setDateTo] = useState(todayIst)
  const showDateRange = DATE_RANGE_MODES.has(mode)

  useEffect(() => {
    const id = setInterval(() => setClock(istClock()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (modeParam && modeParam !== mode) navigate(`/mbmt/reports/${mode}`, { replace: true })
  }, [modeParam, mode, navigate])

  const hint = MBMT_REPORT_MODES.find((m) => m.id === mode)?.hint

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden bg-[#E9EEF5] p-3">
      <TopBar
        clock={clock}
        live
        rate="MBMT reports"
        user={sessionDisplayName(session)}
        fleets={fleetPills(isSuperAdmin)}
        activeFleet="mbmt"
        onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
        nav={surfacePills('mbmt')}
        activeNav="reports"
        onNav={(id) => navigate(pathFor('mbmt', id === 'reports' ? 'reports' : id === 'charging' ? 'charging' : 'live'))}
        onSettings={() => navigate('/settings')}
      />

      <div className={`${reportCard} ${reportCardPad} shrink-0`}>
        <div className={reportSegment} role="tablist" aria-label="MBMT report modes">
          {MBMT_REPORT_MODES.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={mode === tab.id}
              onClick={() => navigate(`/mbmt/reports/${tab.id}`)}
              className={reportSegmentBtn(mode === tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {hint && <p className="mt-2 text-sm text-slate-500">{hint}</p>}
        {showDateRange && (
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
              from
              <input
                type="date" value={dateFrom} max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${reportInput} font-mono`}
              />
            </label>
            <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
              to
              <input
                type="date" value={dateTo} min={dateFrom} max={todayIst()}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${reportInput} font-mono`}
              />
            </label>
            <button
              type="button"
              onClick={() => { const t = todayIst(); setDateFrom(t); setDateTo(t) }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Today
            </button>
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {mode === 'trips' && <TripLogTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'intel' && <RouteScheduleIntelTable />}
        {mode === 'schedule-intel' && <ScheduleIntelTable />}
        {mode === 'live' && <LiveTripsTable />}
        {mode === 'shed' && <ShedTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'punctuality' && <PunctualityTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'alerts' && <AlertsSummaryTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'energy' && <EnergyTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'trip-alerts' && <TripAlertsReportTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'dead-mileage' && <DeadMileageTable dateFrom={dateFrom} dateTo={dateTo} />}
        {mode === 'schedule-trips' && <ScheduleTripLogTable dateFrom={dateFrom} dateTo={dateTo} />}
      </div>
    </div>
  )
}
